<script>
// This is a generic component that shows a form to create or update a task.
// The computed property `formType` is going to return one of these 3 types of tasks:
// - "config-task"
// - "treatment-task"
// - "patient-task"

import cloneDeep from "clone-deep";

import leave from "@/mixins/leave";

import fileUploadConfigService from "@/services/fileupload-config";
import defaultTaskService from "@/services/default-task";
import treatmentTaskService from "@/services/treatment-task";
import patientTaskService from "@/services/patient-task";
import dateHelpersService from "@/services/date-helpers";
import { TASKS_TYPES, getDefaultTaskData } from "@/services/constants";
import segmentService from "@/services/segment";

import Spinner from "@/components/ui/Spinner";
import FileUpload from "@/components/ui/FileUpload";
import TaskPeriodization from "@/components/task/TaskPeriodization";
import TaskNotification from "@/components/task/TaskNotification";
import TaskDefaults from "@/components/task/TaskDefaults";
import TaskDetailHeader from "@/components/task/TaskDetailHeader";
import TaskGeneralForm from "@/components/task/TaskGeneralForm";
import TaskMeasurement from "@/components/task/TaskMeasurement";
import TaskLinks from "@/components/task/TaskLinks";
import TaskCheckinsForm from "@/components/task/TaskCheckinsForm";
import TaskHealthConversationForm from "@/components/task/TaskHealthConversationForm.vue";
import TaskAppointmentsForm from "@/components/task/TaskAppointmentsForm";
import TaskReportsForm from "@/components/task/TaskReportsForm";
import TaskExercisesForm from "@/components/task/TaskExercisesForm";
import TaskRecommendationsForm from "@/components/task/TaskRecommendationsForm";
import TaskMedicationsForm from "@/components/task/TaskMedicationsForm";
import TaskMedia from "@/components/task/TaskMedia";

/**
 * @component TaskDetail
 * @description Form de alta / edición de una tarea (configuración, en tratamiento, o de paciente).
 *              Layout actual: page-header + tabs (General / Periodización / Media y links),
 *              consistente con `ContractDetail.vue` y `PlanDetail.vue`.
 *              Layout legacy disponible como fallback via `localStorage.taskDetailLegacyLayout = '1'`
 *              (ver computed `useLegacyLayout`).
 */
export default {
  name: "TaskDetail",

  components: {
    FileUpload,
    Spinner,
    TaskPeriodization,
    TaskNotification,
    TaskDefaults,
    TaskDetailHeader,
    TaskGeneralForm,
    TaskMeasurement,
    TaskLinks,
    TaskCheckinsForm,
    TaskHealthConversationForm,
    TaskAppointmentsForm,
    TaskReportsForm,
    TaskExercisesForm,
    TaskRecommendationsForm,
    TaskMedicationsForm,
    TaskMedia,
  },

  mixins: [leave],

  data() {
    return {
      TASKS_TYPES,
      treatmentID: this.$route.params.id, // TODO: refactor this to be params.treatmentID
      patientID: this.$route.params.id, // TODO: refactor this to be params.patientID
      taskID: this.$route.params.taskID,
      taskType: this.$route.params.taskType,
      task: getDefaultTaskData(this.$route.params.taskType),
      displayName: TASKS_TYPES[this.$route.params.taskType].name,
      icon: TASKS_TYPES[this.$route.params.taskType].icon,
      isUpdate: this.$route.name.startsWith("update"),
      isFetching: false,
      isPosting: false,
      files: [],
      activeTab: "general",
    };
  },

  created() {
    document.addEventListener("keyup", this.onKeyUp);
    if (this.isUpdate) {
      this.getTask();
    }
  },

  beforeDestroy() {
    this.task = getDefaultTaskData(this.taskType);
    this.isFetching = false;
    this.isPosting = false;
    document.removeEventListener("keyup", this.onKeyUp);
  },

  computed: {
    formType() {
      switch (this.$route.name) {
        case "create-task":
        case "update-task":
          return "config-task";

        case "create-treatment-task":
        case "update-treatment-task":
          return "treatment-task";

        case "create-patient-task":
        case "update-patient-task":
          return "patient-task";

        default:
          return "";
      }
    },

    showConfigs() {
      return (
        this.formType !== "config-task" && TASKS_TYPES[this.taskType].hasConfig
      );
    },

    showMedia() {
      return TASKS_TYPES[this.taskType].hasMedia;
    },

    showPeriodization() {
      return this.formType === "treatment-task" && this.taskType !== "reports";
    },

    /**
     * @description Tab "Media y links" sólo cuando aplica al menos uno.
     *              Para taskType === 'reports' la devolución al paciente vive
     *              dentro de TaskReportsForm (en el tab General), así que el tab
     *              compartido no se muestra.
     * @returns {boolean}
     */
    showMediaLinksTab() {
      return this.showMedia || this.taskType !== "reports";
    },

    /**
     * @description Fallback al layout viejo (modal fixed) via localStorage.
     *              Para volver al viejo:
     *                localStorage.setItem('taskDetailLegacyLayout', '1') y reload.
     *              Para volver al nuevo:
     *                localStorage.removeItem('taskDetailLegacyLayout') y reload.
     * @returns {boolean}
     */
    useLegacyLayout() {
      try {
        return (
          typeof localStorage !== "undefined" &&
          localStorage.getItem("taskDetailLegacyLayout") === "1"
        );
      } catch (e) {
        return false;
      }
    },

    /**
     * @description Texto del breadcrumb de back: nombre del tipo de tarea
     *              (Recomendaciones, Medicación, etc.) según TASKS_TYPES.
     * @returns {string}
     */
    parentTitle() {
      return TASKS_TYPES[this.taskType]
        ? TASKS_TYPES[this.taskType].name
        : "Tareas";
    },

    /**
     * @description Título principal del page-header en el layout nuevo.
     * @returns {string}
     */
    pageTitle() {
      return this.isUpdate ? "Editar tarea" : "Nueva tarea";
    },

    uploadMediaEndpoint() {
      if (this.formType === "config-task") {
        return fileUploadConfigService.getTaskMediaEndpoint(
          this.taskType,
          "default",
          this.taskID
        );
      }

      if (this.formType === "treatment-task") {
        return fileUploadConfigService.getTaskMediaEndpoint(
          this.taskType,
          "treatment",
          this.taskID
        );
      }

      return fileUploadConfigService.getTaskMediaEndpoint(
        this.taskType,
        "patient",
        this.taskID
      );
    },
  },

  methods: {
    onKeyUp(event) {
      const esc = 27;
      if (event.keyCode == esc) {
        this.closeModal();
      }
    },

    closeModal() {
      this.$router.back();
    },

    getTask() {
      this.isFetching = true;
      let serviceCall;

      const options = {
        taskID: this.taskID,
        taskType: this.taskType,
      };

      if (this.formType === "config-task") {
        serviceCall = defaultTaskService.getById(options);
      } else if (this.formType === "treatment-task") {
        serviceCall = treatmentTaskService.getById(options);
      } else {
        serviceCall = patientTaskService.getById(options);
      }

      return serviceCall
        .then((task) => {
          if (this.showPeriodization && task.periodization.hours.length) {
            task.periodization.hours = this.getPeriozationHoursInLocal(task);
          }
          this.task = { ...this.task, ...task };
        })
        .finally(() => {
          this.isFetching = false;
        });
    },

    isValidPeriodization() {
      if (this.showPeriodization) {
        return (
          this.task.periodization.days.length &&
          this.task.periodization.hours.length
        );
      } else {
        return true;
      }
    },

    submit() {
      if (this.isValidPeriodization()) {
        this.upsertTask();
      } else if (!this.useLegacyLayout) {
        // Layout nuevo: si la periodización no es válida, mostrar el tab
        // donde está para que el usuario vea el error.
        this.activeTab = "schedule";
      }
    },

    getUpsertTaskServiceMethod(task) {
      const action = this.isUpdate ? "update" : "create";

      if (this.formType === "config-task") {
        return defaultTaskService[action](task, this.taskType);
      }

      if (this.formType === "treatment-task") {
        return treatmentTaskService[action]({
          taskID: this.taskID,
          taskType: this.taskType,
          treatmentID: this.treatmentID,
          task,
        });
      }

      if (this.formType === "patient-task") {
        return patientTaskService[action]({
          taskID: this.taskID,
          taskType: this.taskType,
          patientID: this.patientID,
          task,
        });
      }
    },

    upsertTask() {
      const eventName = this.isUpdate ? "Task Updated" : "Task Created";
      segmentService.track({ name: eventName });

      const isReport = this.taskType === "reports";
      this.isPosting = true;
      this.showLeaveGuard = false;

      const task = cloneDeep(this.task);

      if (this.showPeriodization) {
        task.periodization.hours = this.getPeriozationHoursInUTC(task);

        if (task.periodization.type === "explicit") {
          delete task.periodization.fromDay;
          delete task.periodization.toDay;
        }
      }

      task.links = (task.links || []).filter(
        (link) => link && link.name && link.url
      );

      if (isReport) {
        delete task.recommendations;
      }

      const serviceCall = this.getUpsertTaskServiceMethod(task);

      return serviceCall
        .then(({ data }) => {
          this.taskID = this.taskID || data._id;
          const createdTask = data || task;
          this.$bus.$emit("task-update", {
            ...createdTask,
            type: this.taskType,
          });

          if (isReport) {
            return this.$refs.taskReportsForm.saveRecommendations(this.taskID);
          }

          if (this.$refs.taskMedia) {
            this.$refs.taskMedia.uploadFiles();
          }
        })
        .finally(() => {
          // taskMedia component is going to call closeModal when finished
          if (!this.$refs.taskMedia) {
            this.isPosting = false;
            this.closeModal();
          }
        });
    },

    getPeriozationHoursInUTC(task) {
      return task.periodization.hours.map((time) =>
        dateHelpersService.localTimeToUTC(time)
      );
    },

    getPeriozationHoursInLocal(task) {
      return task.periodization.hours.map((time) =>
        dateHelpersService.UTCTimeToLocal(time)
      );
    },

    onMediaError() {
      this.isPosting = false;
    },
  },
};
</script>

<template lang="pug">
  ValidationObserver(v-slot="{handleSubmit}")
    //- ─────────────────────────────────────────────────────────────────
    //- LEGACY LAYOUT (fallback) — modal fixed con header azul, botones
    //- flotantes. Activable via `localStorage.taskDetailLegacyLayout='1'`.
    //- Se mantiene byte-por-byte como estaba para que sea un escape válido
    //- si el layout nuevo da problemas.
    //- ─────────────────────────────────────────────────────────────────
    form.modal(v-if="useLegacyLayout" @submit.prevent="handleSubmit(submit)")
      task-detail-header(
        :is-update="isUpdate"
        :is-submit="true"
        :is-loading="isPosting"
        :display-name="displayName"
        :close="closeModal"
      )
      p.modal__subtitle Los campos con (*) son obligatorios

      .modal__content(v-if="isFetching")
        spinner

      .modal__content(v-else)
        .modal__block
          .modal__section
            .modal__sign.sign
              .sign__icon
                micon(:name="icon")
              h3.sign__title Información General

            article.modal__fields
              task-defaults(v-if="showConfigs" v-model="task" :type="taskType")
              task-general-form(v-model="task" :type="taskType")
              task-appointments-form(v-if="taskType === 'appointments'" v-model="task")
              task-recommendations-form(v-if="taskType === 'recommendations'" v-model="task" :is-update="isUpdate")
              task-medications-form(v-if="taskType === 'medications'" v-model="task")

            article.modal__fields

        task-exercises-form(v-if="taskType === 'exercises'" v-model="task")
        task-checkins-form(v-if="taskType === 'checkins'" v-model="task")
        task-health-conversation-form(v-if="taskType === 'healthConversations'" v-model="task")
        task-measurement(v-model="task" :type="taskType")

        task-media(
          v-if="showMedia"
          ref="taskMedia"
          v-model="task"
          :upload-media-endpoint="uploadMediaEndpoint"
          :close-modal="closeModal"
          :on-media-error="onMediaError"
          :type="taskType"
        )

        task-links(v-if="taskType !== 'reports'" v-model="task")

        task-reports-form(
          v-if="taskType === 'reports'"
          ref="taskReportsForm"
          v-model="task"
          :is-update="isUpdate"
          :form-type="formType"
          :close-modal="closeModal"
        )

        task-periodization(v-if="showPeriodization" v-model="task")
        task-notification(v-model="task" :showPeriodization="showPeriodization")

    //- ─────────────────────────────────────────────────────────────────
    //- NEW LAYOUT — page-header + tabs. Patrón consistente con
    //- ContractDetail.vue / PlanDetail.vue.
    //- ─────────────────────────────────────────────────────────────────
    form.task-detail(v-else @submit.prevent="handleSubmit(submit)")
      .page-header
        .page-header__left
          el-button(type="text" icon="el-icon-arrow-left" @click="closeModal") {{ parentTitle }}
          h1.page-title {{ pageTitle }}
        .page-header__actions
          el-button(@click="closeModal") Cancelar
          el-button(
            type="primary"
            :loading="isPosting"
            native-type="submit"
          ) {{ isUpdate ? 'Guardar cambios' : 'Crear' }}

      p.page-hint Los campos con (*) son obligatorios

      .task-detail__loading(v-if="isFetching")
        spinner

      el-tabs.task-detail__tabs(v-else v-model="activeTab")

        //- ── Tab General: defaults + general + form específico de tipo + measurement ──
        el-tab-pane(label="General" name="general")
          .task-detail__pane
            task-defaults(v-if="showConfigs" v-model="task" :type="taskType")
            task-general-form(v-model="task" :type="taskType")
            task-appointments-form(v-if="taskType === 'appointments'" v-model="task")
            task-recommendations-form(v-if="taskType === 'recommendations'" v-model="task" :is-update="isUpdate")
            task-medications-form(v-if="taskType === 'medications'" v-model="task")
            task-exercises-form(v-if="taskType === 'exercises'" v-model="task")
            task-checkins-form(v-if="taskType === 'checkins'" v-model="task")
            task-health-conversation-form(
              v-if="taskType === 'healthConversations'"
              v-model="task"
            )
            task-reports-form(
              v-if="taskType === 'reports'"
              ref="taskReportsForm"
              v-model="task"
              :is-update="isUpdate"
              :form-type="formType"
              :close-modal="closeModal"
            )
            task-measurement(v-model="task" :type="taskType")

        //- ── Tab Periodización + Notificación ──
        el-tab-pane(label="Periodización y notificación" name="schedule")
          .task-detail__pane
            task-periodization(v-if="showPeriodization" v-model="task")
            task-notification(v-model="task" :showPeriodization="showPeriodization")

        //- ── Tab Media y links (oculto para reports — su devolución vive en TaskReportsForm) ──
        el-tab-pane(label="Media y links" name="media" v-if="showMediaLinksTab")
          .task-detail__pane
            task-media(
              v-if="showMedia"
              ref="taskMedia"
              v-model="task"
              :upload-media-endpoint="uploadMediaEndpoint"
              :close-modal="closeModal"
              :on-media-error="onMediaError"
              :type="taskType"
            )
            task-links(v-if="taskType !== 'reports'" v-model="task")
</template>

<style lang="scss" scoped>
.task-detail {
  padding: 24px 32px 48px;
  background: #f6f8fb;
  min-height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 4px;
}

.page-header__left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.page-header__actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.page-title {
  font-size: 22px;
  font-weight: 700;
  margin: 0;
  color: #1a2540;
  letter-spacing: -0.01em;
}

.page-hint {
  font-size: 12px;
  color: #97a0b5;
  margin: 0 0 16px;
}

.task-detail__loading {
  background: #fff;
  border-radius: 8px;
  padding: 60px 0;
  display: flex;
  justify-content: center;
}

.task-detail__tabs {
  background: #fff;
  border-radius: 8px;
  padding: 0 20px;
  box-shadow: 0 1px 2px rgba(20, 30, 60, 0.04),
    0 4px 16px rgba(20, 30, 60, 0.04);
}

.task-detail__pane {
  padding: 16px 4px 24px;
}
</style>
